import React, { useState } from "react";
import { Link } from "react-router-dom";
import Blog from "./Blog";
import Faq from "./Faq";
import axios from "axios";
import config from "../config";
import { useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import placeholderimage from "../image/placeholder.jpg";
import TrendingGym from "./TrendingGym";
import FeaturedGym from "./FeaturedGym";
import { CategorySkeleton } from "./DiscoverySkeleton";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchGymCategory } from "../redux/slice/GymCategory";
import membershipGuidance from "../image/membership-offers/membership-guidance-v1.webp";
import appstore from "../image/meragym-app-store-badge.png";
import playstore from "../image/meragym-google-play-badge.png";
import heroImage from "../image/meragym-hero-v2.png";

function DiscoverySectionHeader({ title, description, to, action = "See all" }) {
  return (
    <div className="mg-discovery-heading">
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <Link to={to} className="mg-discovery-heading__link">
        {action}<i className="bi bi-arrow-right" aria-hidden="true"></i>
      </Link>
    </div>
  );
}

export default function Homepage() {
  const dispatch = useDispatch();
  const { categoryData, isLoading } = useSelector(
    (state) => state.GymCategory
  );
  useEffect(() => {
    dispatch(fetchGymCategory());
  }, [dispatch]);

  //Searching the gym and navigating to the list page
  const [search, setSearch] = useState("");
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const updateStickyCta = () => setShowStickyCta(window.scrollY > window.innerHeight * 0.85);
    updateStickyCta();
    window.addEventListener("scroll", updateStickyCta, { passive: true });
    return () => window.removeEventListener("scroll", updateStickyCta);
  }, []);

  //Fetching the availbale city
  const [city, setCity] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}/city-list`);
        setCity(response.data.city_list);
      } catch (error) {
        // silently fail — cities list stays empty
      }
    };
    fetchData();
  }, []);

  //Categroy responsive
  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 1921 },
      items: 8,
      slidesToSlide: 2,
    },
    desktop: {
      breakpoint: { max: 1920, min: 1025 },
      items: 7,
      slidesToSlide: 2,
    },
    tablet: {
      breakpoint: { max: 1024, min: 577 },
      items: 6,
      slidesToSlide: 1,
    },
    mobile: {
      breakpoint: { max: 576, min: 0 },
      items: 3.5,
      slidesToSlide: 1,
    },
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>MeraGym - Find Your Ideal Gym | Best Fitness Centers Near You</title>
          <meta
            name="keywords"
            content="gym near me, fitness center near me, best gym in India, yoga studio near me, CrossFit gym near me, zumba classes near me, gym membership, personal trainer near me, weight loss gym, bodybuilding gym, fitness club near me, health club near me, ladies gym near me, unisex gym near me, gym for beginners, gym with swimming pool, cardio gym, strength training gym, functional fitness center, MMA gym near me, boxing gym near me, pilates studio, aerobics classes, gym in Varanasi, gym in Delhi, gym in Mumbai, gym in Bangalore, gym in Hyderabad, gym in Chennai, gym in Pune, gym in Kolkata, gym in Jaipur, gym in Lucknow, cheap gym membership, affordable gym near me, premium gym near me, 24 hour gym, open gym, gym with sauna, gym with steam room, gym with personal training, online gym booking, find gym, compare gyms, gym reviews India, meragym, mera gym"
          />
          <meta
            name="description"
            content="Find the best gyms, yoga studios, CrossFit centers, and fitness clubs near you on MeraGym. Browse 5,000+ verified gyms across India, compare memberships, read real reviews, and book your free trial today!"
          />
          <link rel="canonical" href="https://meragym.com" />
          <meta property="og:title" content="MeraGym - Find the Best Gyms Near You" />
          <meta property="og:description" content="Discover top-rated gyms, yoga centers, and fitness studios in your city. Compare, review, and join the perfect gym." />
          <meta property="og:url" content="https://meragym.com" />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="https://meragym.com/og-home.jpg" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="MeraGym - Find the Best Gyms Near You" />
          <meta name="twitter:description" content="Discover top-rated gyms, yoga centers, and fitness studios in your city." />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "name": "MeraGym",
                "url": "https://meragym.com",
                "description": "India's leading gym discovery platform — find gyms, yoga studios, and fitness centers near you.",
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": { "@type": "EntryPoint", "urlTemplate": "https://meragym.com/search/{search_term_string}" },
                  "query-input": "required name=search_term_string"
                }
              },
              {
                "@type": "Organization",
                "name": "MeraGym",
                "url": "https://meragym.com",
                "logo": "https://meragym.com/logo.png",
                "sameAs": [
                  "https://www.facebook.com/meragym/",
                  "https://www.instagram.com/meragymofficial/",
                  "https://www.youtube.com/@meragym/"
                ]
              }
            ]
          })}</script>
        </Helmet>
      </HelmetProvider>
      <section className="homepage-hero" style={{ "--hero-image": `url(${heroImage})` }}>
        <div className="homepage-hero__veil" aria-hidden="true"></div>
        <div className="container homepage-hero__container">
          <div className="homepage-hero__content">
            <span className="homepage-hero__eyebrow">
              <i className="bi bi-patch-check-fill" aria-hidden="true"></i>
              India&apos;s trusted gym finder
            </span>
            <h1 className="homepage-hero__title">
              Find a gym that <span>fits your life.</span>
            </h1>
            <p className="homepage-hero__subtitle">
              Compare trusted fitness centres, real member reviews, and membership plans near you.
            </p>

            <div className="homepage-hero__search" role="search">
              <i className="bi bi-geo-alt-fill homepage-hero__search-icon" aria-hidden="true"></i>
              <label className="visually-hidden" htmlFor="gym-location">City or area</label>
              <input
                id="gym-location"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Enter city or neighbourhood"
              />
              <Link
                to={search.trim() === "" ? "/gyms" : `/search/${search.trim()}`}
                className="homepage-hero__search-button"
                aria-label="Search for gyms"
              >
                <span>Find gyms</span><i className="bi bi-arrow-right" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="homepage-hero__quick-links" aria-label="Popular gym locations">
              <span>Popular:</span>
              <Link to="/delhi/gym">Delhi</Link>
              <Link to="/mumbai/gym">Mumbai</Link>
              <Link to="/pune/gym">Pune</Link>
              <Link to="/varanasi/gym">Varanasi</Link>
            </div>

            <div className="homepage-hero__proof" aria-label="MeraGym platform highlights">
              <div><strong>5,000+</strong><span>verified gyms</span></div>
              <div><strong>50+</strong><span>cities covered</span></div>
              <div><strong>4.8 <i className="bi bi-star-fill" aria-hidden="true"></i></strong><span>member rating</span></div>
            </div>
          </div>
        </div>
      </section>
      {/* B2B / app strip removed — was interrupting gym-finder flow */}
      <div className="container-fluid offBg">
        <div className="container head-row px-0">
          <div className="mg-home-section-heading">
            <p className="mg-home-section-heading__eyebrow">Explore by workout</p>
            <h2>Find your kind of fitness</h2>
            <p className="mg-home-section-heading__description">From strength training to yoga, find a space that matches your routine.</p>
          </div>
          {isLoading ? (
            <CategorySkeleton />
          ) : (
            <>
              <div className="off-sec">
                <Carousel
                  responsive={responsive}
                  swipeable={true}
                  draggable={true}
                  infinite={false}
                  showDots={false}
                  autoPlay={false}
                  autoPlaySpeed={3000}
                  removeArrowOnDeviceType={["mobile"]}
                >
                  {categoryData.map((cData) => {
                    return (
                      <Link
                        to={`/gyms/${cData.slug}`}
                        className="cat-list"
                        key={cData.id}
                      >
                        <div className="d-flex flex-column text-center">
                          <div className="gym-category-circle">
                            <img
                              src={cData.image || placeholderimage}
                              onError={(e) => { e.target.src = placeholderimage; }}
                              loading="lazy"
                              className="gym-category-pic"
                              alt={cData.name}
                            />
                          </div>
                          <span className="category-name">{cData.name}</span>
                        </div>
                      </Link>
                    );
                  })}
                </Carousel>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="container-fluid">
        <div className="container px-0">
          <DiscoverySectionHeader
            title="Trending fitness centres"
            description="Popular places people are checking out right now."
            to="/gyms"
          />
          <TrendingGym />
        </div>

        <div className="container px-0">
          <DiscoverySectionHeader
            title="Featured fitness centres"
            description="Standout places to train, selected for your next session."
            to="/gyms"
          />
          <FeaturedGym />
        </div>

        <div className="container px-0">
          <DiscoverySectionHeader
            title="The fitness blog"
            description="Straightforward guidance for getting more from your routine."
            to="/blog"
            action="Read more"
          />
          <Blog />
        </div>

        {/* Gym Software Banner */}
        <div className="gymsoft-banner-section">
          <div className="gymsoft-glow gymsoft-glow-1"></div>
          <div className="gymsoft-glow gymsoft-glow-2"></div>
          <div className="container">
            {/* Top label */}
            <div className="gymsoft-top-label">
              <span className="gymsoft-badge">
                <i className="bi bi-stars me-1"></i> All-in-One Gym Management SaaS
              </span>
            </div>

            <div className="gymsoft-banner-inner">
              {/* Left: content */}
              <div className="gymsoft-banner-left">
                <h2 className="gymsoft-heading">
                  Run Your Gym Like a<br />
                  <span className="gymsoft-highlight">Pro Business</span>
                </h2>
                <p className="gymsoft-subtext">
                  MeraGym Software gives gym owners a complete command center — automate billing, track attendance, manage trainers, and never miss a renewal. Trusted by <strong>5,000+ gyms</strong> across India.
                </p>

                <div className="gymsoft-feature-grid">
                  <div className="gymsoft-feature-card">
                    <div className="gymsoft-feature-icon"><i className="bi bi-people-fill"></i></div>
                    <div>
                      <div className="gymsoft-feature-title">Member Management</div>
                      <div className="gymsoft-feature-desc">Profiles, history &amp; plans</div>
                    </div>
                  </div>
                  <div className="gymsoft-feature-card">
                    <div className="gymsoft-feature-icon"><i className="bi bi-credit-card-fill"></i></div>
                    <div>
                      <div className="gymsoft-feature-title">Auto Billing</div>
                      <div className="gymsoft-feature-desc">Collect payments on autopilot</div>
                    </div>
                  </div>
                  <div className="gymsoft-feature-card">
                    <div className="gymsoft-feature-icon"><i className="bi bi-bell-fill"></i></div>
                    <div>
                      <div className="gymsoft-feature-title">Renewal Reminders</div>
                      <div className="gymsoft-feature-desc">Auto SMS &amp; WhatsApp alerts</div>
                    </div>
                  </div>
                  <div className="gymsoft-feature-card">
                    <div className="gymsoft-feature-icon"><i className="bi bi-calendar-check-fill"></i></div>
                    <div>
                      <div className="gymsoft-feature-title">Attendance Tracking</div>
                      <div className="gymsoft-feature-desc">Biometric &amp; app check-ins</div>
                    </div>
                  </div>
                  <div className="gymsoft-feature-card">
                    <div className="gymsoft-feature-icon"><i className="bi bi-journal-text"></i></div>
                    <div>
                      <div className="gymsoft-feature-title">Day Book</div>
                      <div className="gymsoft-feature-desc">Daily income &amp; expense log</div>
                    </div>
                  </div>
                  <div className="gymsoft-feature-card">
                    <div className="gymsoft-feature-icon"><i className="bi bi-receipt"></i></div>
                    <div>
                      <div className="gymsoft-feature-title">Invoice Management</div>
                      <div className="gymsoft-feature-desc">Generate &amp; share instantly</div>
                    </div>
                  </div>
                  <div className="gymsoft-feature-card">
                    <div className="gymsoft-feature-icon"><i className="bi bi-person-badge-fill"></i></div>
                    <div>
                      <div className="gymsoft-feature-title">Trainer Management</div>
                      <div className="gymsoft-feature-desc">Schedule, pay &amp; review trainers</div>
                    </div>
                  </div>
                  <div className="gymsoft-feature-card">
                    <div className="gymsoft-feature-icon"><i className="bi bi-bar-chart-fill"></i></div>
                    <div>
                      <div className="gymsoft-feature-title">Reports &amp; Analytics</div>
                      <div className="gymsoft-feature-desc">Revenue, growth &amp; insights</div>
                    </div>
                  </div>
                </div>

                <div className="gymsoft-cta-row">
                  <a
                    href="https://app.meragym.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gymsoft-cta-btn"
                  >
                    Start Free Trial <i className="bi bi-arrow-right ms-2"></i>
                  </a>
                  <a
                    href="https://app.meragym.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gymsoft-cta-secondary"
                  >
                    <i className="bi bi-play-circle me-1"></i> Watch Demo
                  </a>
                </div>
                <p className="gymsoft-no-cc">No credit card required &nbsp;·&nbsp; Free setup &nbsp;·&nbsp; Cancel anytime</p>
              </div>

              {/* Right: stats + trust */}
              <div className="gymsoft-banner-right">
                <div className="gymsoft-stats">
                  <div className="gymsoft-stat-item">
                    <div className="gymsoft-stat-icon"><i className="bi bi-buildings-fill"></i></div>
                    <strong>5,000+</strong>
                    <span>Gyms Onboarded</span>
                  </div>
                  <div className="gymsoft-stat-item">
                    <div className="gymsoft-stat-icon"><i className="bi bi-emoji-smile-fill"></i></div>
                    <strong>98%</strong>
                    <span>Satisfaction Rate</span>
                  </div>
                  <div className="gymsoft-stat-item">
                    <div className="gymsoft-stat-icon"><i className="bi bi-lightning-charge-fill"></i></div>
                    <strong>₹0</strong>
                    <span>Setup Cost</span>
                  </div>
                  <div className="gymsoft-stat-item">
                    <div className="gymsoft-stat-icon"><i className="bi bi-headset"></i></div>
                    <strong>24/7</strong>
                    <span>Support</span>
                  </div>
                </div>
                <div className="gymsoft-trust-row">
                  <i className="bi bi-shield-fill-check"></i> Secure &amp; Reliable Platform
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why MeraGym Features Section */}
        <section className="mg-features-section">
          <div className="mg-features-bg-decor" aria-hidden="true">
            <span className="mg-features-decor-circle mg-features-decor-1"></span>
            <span className="mg-features-decor-circle mg-features-decor-2"></span>
          </div>
          <div className="container" style={{position:"relative"}}>
            <div className="mg-features-header">
              <span className="mg-features-pill"><i className="bi bi-stars me-1"></i>Why MeraGym?</span>
              <h2 className="mg-features-title">Everything You Need to Find the <span className="text-danger">Perfect Gym</span></h2>
              <p className="mg-features-subtitle">India's smartest gym discovery platform — built for real fitness seekers across 50+ cities.</p>
            </div>

            <div className="mg-features-grid">
              <div className="mg-feature-card">
                <div className="mg-feature-card-top">
                  <div className="mg-feature-icon-wrap">
                    <i className="bi bi-search-heart"></i>
                  </div>
                  <span className="mg-feature-stat">5,000+ Gyms</span>
                </div>
                <h3 className="mg-feature-card-title">Discover Nearby Gyms</h3>
                <p className="mg-feature-card-desc">Browse verified gyms, yoga studios, CrossFit boxes, and fitness centers across India — all in one place.</p>
                <div className="mg-feature-card-footer">
                  <i className="bi bi-arrow-right-circle-fill"></i> Search now
                </div>
              </div>

              <div className="mg-feature-card">
                <div className="mg-feature-card-top">
                  <div className="mg-feature-icon-wrap">
                    <i className="bi bi-star-fill"></i>
                  </div>
                  <span className="mg-feature-stat">Real Reviews</span>
                </div>
                <h3 className="mg-feature-card-title">Verified Reviews</h3>
                <p className="mg-feature-card-desc">Read honest member reviews before you join. No fake ratings — just real, moderated feedback from actual gym-goers.</p>
                <div className="mg-feature-card-footer">
                  <i className="bi bi-arrow-right-circle-fill"></i> Read reviews
                </div>
              </div>

              <div className="mg-feature-card">
                <div className="mg-feature-card-top">
                  <div className="mg-feature-icon-wrap">
                    <i className="bi bi-bar-chart-line-fill"></i>
                  </div>
                  <span className="mg-feature-stat">Best Deals</span>
                </div>
                <h3 className="mg-feature-card-title">Compare Plans & Prices</h3>
                <p className="mg-feature-card-desc">Compare membership plans, facilities, and pricing side-by-side so you always get the best value for your budget.</p>
                <div className="mg-feature-card-footer">
                  <i className="bi bi-arrow-right-circle-fill"></i> Compare gyms
                </div>
              </div>

              <div className="mg-feature-card">
                <div className="mg-feature-card-top">
                  <div className="mg-feature-icon-wrap">
                    <i className="bi bi-funnel-fill"></i>
                  </div>
                  <span className="mg-feature-stat">20+ Filters</span>
                </div>
                <h3 className="mg-feature-card-title">Smart Filters</h3>
                <p className="mg-feature-card-desc">Filter by type, city, amenities like pool or sauna, ladies-only, AC, 24-hour access, and budget range.</p>
                <div className="mg-feature-card-footer">
                  <i className="bi bi-arrow-right-circle-fill"></i> Use filters
                </div>
              </div>

              <div className="mg-feature-card">
                <div className="mg-feature-card-top">
                  <div className="mg-feature-icon-wrap">
                    <i className="bi bi-person-check-fill"></i>
                  </div>
                  <span className="mg-feature-stat">Certified Pros</span>
                </div>
                <h3 className="mg-feature-card-title">Find Certified Trainers</h3>
                <p className="mg-feature-card-desc">Connect with certified personal trainers in your city for customised workout plans, diet advice, and coaching.</p>
                <div className="mg-feature-card-footer">
                  <i className="bi bi-arrow-right-circle-fill"></i> Find trainers
                </div>
              </div>

              <div className="mg-feature-card mg-feature-card--free">
                <div className="mg-feature-card-top">
                  <div className="mg-feature-icon-wrap">
                    <i className="bi bi-shield-fill-check"></i>
                  </div>
                  <span className="mg-feature-stat">₹0 Cost</span>
                </div>
                <h3 className="mg-feature-card-title">100% Free to Use</h3>
                <p className="mg-feature-card-desc">No hidden charges, no sign-up fees. Discover, compare, and connect with gyms near you — completely free, forever.</p>
                <div className="mg-feature-card-footer">
                  <i className="bi bi-arrow-right-circle-fill"></i> Get started free
                </div>
              </div>
            </div>

            {/* Trust Stats Bar */}
            <div className="mg-features-stats-bar">
              <div className="mg-features-stat-item">
                <strong>5,000+</strong>
                <span>Gyms Listed</span>
              </div>
              <div className="mg-features-stat-divider"></div>
              <div className="mg-features-stat-item">
                <strong>50+</strong>
                <span>Cities Covered</span>
              </div>
              <div className="mg-features-stat-divider"></div>
              <div className="mg-features-stat-item">
                <strong>1 Lakh+</strong>
                <span>Happy Members</span>
              </div>
              <div className="mg-features-stat-divider"></div>
              <div className="mg-features-stat-item">
                <strong>4.8 ★</strong>
                <span>Average Rating</span>
              </div>
              <div className="mg-features-stat-divider"></div>
              <div className="mg-features-stat-item">
                <strong>₹0</strong>
                <span>To Get Started</span>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="hiw-section">
          <div className="container">
            <div className="hiw-header">
              <span className="hiw-pill">How It Works</span>
              <h2 className="hiw-title">Find Your Perfect Gym in <span className="text-danger">3 Simple Steps</span></h2>
              <p className="hiw-subtitle">No registration fees. No confusion. Just find, compare, and join.</p>
            </div>
            <div className="hiw-steps">
              <div className="hiw-step">
                <div className="hiw-step-num">1</div>
                <div className="hiw-step-icon"><i className="bi bi-search"></i></div>
                <h3 className="hiw-step-title">Search Your City</h3>
                <p className="hiw-step-desc">Enter your city or area in the search bar and instantly browse 5,000+ verified gyms, yoga studios, and fitness centers near you.</p>
              </div>
              <div className="hiw-connector"><i className="bi bi-arrow-right"></i></div>
              <div className="hiw-step">
                <div className="hiw-step-num">2</div>
                <div className="hiw-step-icon"><i className="bi bi-bar-chart-line"></i></div>
                <h3 className="hiw-step-title">Compare & Choose</h3>
                <p className="hiw-step-desc">Read verified member reviews, compare membership plans and prices, check facilities like pool, AC, trainers — then pick the best fit.</p>
              </div>
              <div className="hiw-connector"><i className="bi bi-arrow-right"></i></div>
              <div className="hiw-step">
                <div className="hiw-step-num">3</div>
                <div className="hiw-step-icon"><i className="bi bi-patch-check"></i></div>
                <h3 className="hiw-step-title">Join & Get Fit</h3>
                <p className="hiw-step-desc">Contact the gym directly, book a free trial, and start your fitness journey — completely free, no middlemen, no hidden fees.</p>
              </div>
            </div>
          </div>
        </section>

        {/* App Download Section */}
        <section className="mg-app-v2" aria-labelledby="mg-app-heading">
          <div className="mg-app-v2__orb mg-app-v2__orb--one"></div><div className="mg-app-v2__orb mg-app-v2__orb--two"></div>
          <div className="container">
            <div className="mg-app-v2__inner">
              <div className="mg-app-v2__copy">
                <span className="mg-app-v2__eyebrow"><i className="bi bi-phone-fill"></i> Available on mobile</span>
                <h2 id="mg-app-heading">Take MeraGym <span>everywhere you go.</span></h2>
                <p>Find a new place to train, stay on top of your membership, and keep your fitness plans close—whether you’re at home, work, or already on the move.</p>
                <div className="mg-app-v2__feature-grid">
                  <div><i className="bi bi-search-heart-fill"></i><span><strong>Discover nearby</strong>Find your next place to train</span></div>
                  <div><i className="bi bi-ticket-perforated-fill"></i><span><strong>Member offers</strong>Explore app-friendly deals</span></div>
                  <div><i className="bi bi-calendar2-check-fill"></i><span><strong>Stay on track</strong>Keep membership details handy</span></div>
                  <div><i className="bi bi-person-heart"></i><span><strong>Find guidance</strong>Connect with fitness pros</span></div>
                </div>
                <div className="mg-app-v2__badges">
                  <a href="https://play.google.com/store/apps/details?id=com.techup.mygym" target="_blank" rel="noreferrer" className="mg-app-badge-link">
                    <img src={playstore} alt="Download MeraGym on Google Play Store" />
                  </a>
                  <a href="https://apps.apple.com" target="_blank" rel="noreferrer" className="mg-app-badge-link">
                    <img src={appstore} alt="Download MeraGym on Apple App Store" />
                  </a>
                </div>
                <small><i className="bi bi-check-circle-fill"></i> Free to download. Available for Android and iOS.</small>
              </div>
              <div className="mg-app-v2__visual" aria-label="MeraGym mobile app preview">
                <div className="mg-app-v2__phone">
                  <div className="mg-app-v2__notch"></div>
                  <div className="mg-app-v2__screen">
                    <div className="mg-app-v2__appbar"><span><i className="bi bi-grid-1x2-fill"></i> MeraGym</span><i className="bi bi-bell"></i></div>
                    <p className="mg-app-v2__hello">Good evening</p><h3>What moves you today?</h3>
                    <div className="mg-app-v2__search"><i className="bi bi-search"></i><span>Search gyms near you</span></div>
                    <div className="mg-app-v2__nearby"><div className="mg-app-v2__nearby-art"><i className="bi bi-lightning-charge-fill"></i></div><div><strong>Fitness near you</strong><span>Explore your neighbourhood</span></div><i className="bi bi-arrow-up-right"></i></div>
                    <div className="mg-app-v2__membership"><span><i className="bi bi-ticket-perforated-fill"></i> MEMBER PERKS</span><strong>Your next gym could be closer than you think.</strong><button type="button">Explore offers <i className="bi bi-arrow-right"></i></button></div>
                    <div className="mg-app-v2__nav"><i className="bi bi-house-door-fill"></i><i className="bi bi-search"></i><i className="bi bi-heart"></i><i className="bi bi-person"></i></div>
                  </div>
                </div>
                <div className="mg-app-v2__float mg-app-v2__float--near"><i className="bi bi-geo-alt-fill"></i><span><strong>Find your fit</strong>Nearby, on your terms</span></div>
                <div className="mg-app-v2__float mg-app-v2__float--saved"><i className="bi bi-heart-fill"></i><span><strong>Save favourites</strong>Come back when ready</span></div>
              </div>
            </div>
          </div>
        </section>

        <section className="mg-membership-cta" aria-labelledby="mg-membership-heading">
          <div className="container">
            <div className="mg-membership-cta__inner">
              <div className="mg-membership-cta__visual">
                <img src={membershipGuidance} alt="Fitness coach welcoming a new gym member" />
                <div className="mg-membership-cta__photo-note"><i className="bi bi-heart-pulse-fill"></i><span><strong>Feel at home, faster</strong>Find your right gym and plan.</span></div>
              </div>
              <div className="mg-membership-cta__copy">
                <span className="mg-membership-cta__eyebrow"><i className="bi bi-ticket-perforated-fill"></i> Membership offers near you</span>
                <h2 id="mg-membership-heading">Get our membership and start your fitness journey today.</h2>
                <p>Whether you’re restarting, trying something new, or ready to stay consistent, we’ll help you find a gym membership that works with your routine.</p>
                <div className="mg-membership-cta__perks">
                  <span><i className="bi bi-geo-alt-fill"></i> Explore gyms near you</span>
                  <span><i className="bi bi-tags-fill"></i> Ask about member offers</span>
                  <span><i className="bi bi-chat-heart-fill"></i> Get guidance when needed</span>
                </div>
                <Link to="/gym-membership-discounts-coupon" className="mg-membership-cta__button">
                  Find membership offers <i className="bi bi-arrow-right"></i>
                </Link>
                <small><i className="bi bi-shield-check"></i> No payment required to enquire</small>
              </div>
            </div>
          </div>
        </section>

        <div className="container px-0">
          <h6 className="offHead text-start avail-text head-row">
            Available In
          </h6>
          <ul className="cities-list">
            {city.map((data) => {
              return (
                <li className="list-items" key={data.city}>
                  <Link to={`/${data.city}/gym`}>{data.city}</Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <Faq page="home" />

      {/* Sticky mobile CTA — visible only on small screens when user scrolls past hero */}
      {showStickyCta && (
        <div className="mg-sticky-mobile-cta d-lg-none">
          <Link to="/gyms" className="mg-sticky-find-btn">
            <i className="bi bi-search me-2"></i>Find Gyms Near Me
          </Link>
        </div>
      )}
    </>
  );
}
